import { useState, useEffect, useCallback } from "react";
import { useUser } from "@/hooks/useUser";
import { toggleFavourite, fetchFavouriteIds } from "@/lib/users-api";

const ANON_KEY = "bac_anon_favourites_v1";

export interface AnonymousFavouriteCar {
  id: number;
  make?: string | null;
  model?: string | null;
  year?: number | null;
  slug?: string | null;
  city?: string | null;
  state?: string | null;
  country_code?: string | null;
  primary_image?: string | null;
  price_driving?: number | null;
  /** Task #2360: full price-column set so the logged-out Favourites
   *  page can run the same "from £X / day" waterfall the home
   *  `CarCard` uses for the same car, instead of falling back to
   *  "Price on request". Captured from the CarCard snapshot at
   *  heart-click time. */
  price_event?: number | null;
  minimum_price?: number | null;
  chauffeur_min_price?: number | null;
  event_min_price?: number | null;
  /** Task #(car-1059383): rental_*_active flags captured at
   *  heart-click time so the anon Favourites tile runs the same
   *  inactive-mode-gated `getDisplayPrice` waterfall as the home
   *  card. Undefined on snapshots taken before this field shipped —
   *  the rendering site defaults missing flags to `false` so an old
   *  snapshot with a stale populated disabled-mode column can't
   *  hijack the card. */
  rental_driving_active?: boolean | null;
  rental_chauffeured_active?: boolean | null;
  rental_event_active?: boolean | null;
  local_currency_code?: string | null;
  savedAt: number;
}

export type AnonymousFavouriteSnapshot = Omit<AnonymousFavouriteCar, "id" | "savedAt">;

let cachedIds: Set<number> | null = null;
// User id the cached ids belong to. When the active user changes we
// wipe the cache so user B never inherits user A's favourites in the
// same tab.
let cachedUserId: string | null = null;
let loadingPromise: Promise<void> | null = null;
let anonCars: AnonymousFavouriteCar[] = loadAnon();
const listeners = new Set<() => void>();
let mergeInFlight: Promise<void> | null = null;

function loadAnon(): AnonymousFavouriteCar[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(ANON_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (c): c is AnonymousFavouriteCar =>
        c && typeof c === "object" && typeof c.id === "number",
    );
  } catch {
    return [];
  }
}

function persistAnon(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(ANON_KEY, JSON.stringify(anonCars));
  } catch {
    // private mode / quota exceeded — anon state still works in-memory
  }
}

function notify(): void {
  listeners.forEach((fn) => fn());
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

function useRev(): void {
  const [, setRev] = useState(0);
  useEffect(
    () => subscribe(() => setRev((n) => (n + 1) | 0)),
    [],
  );
}

/**
 * Returns the live list of anonymously-saved cars (newest first).
 * Used by FavouritesPage to render logged-out users' temp picks.
 */
export function useAnonymousFavouriteCars(): AnonymousFavouriteCar[] {
  useRev();
  return anonCars.slice().sort((a, b) => b.savedAt - a.savedAt);
}

/**
 * Returns the merged set of favourited car ids for the current user
 * (server-side when signed in, localStorage when not). Triggers the
 * one-time sign-in merge of anon favourites into the server list.
 */
export function useFavouriteIds(): Set<number> {
  const { session, user } = useUser();
  useRev();

  useEffect(() => {
    const token = session?.access_token;
    const uid = user?.id ?? null;
    // Auth transition: a different user (or sign-out) invalidates the
    // module-global cache that was hydrated for the previous user.
    if (cachedUserId !== uid) {
      cachedIds = null;
      cachedUserId = uid;
      loadingPromise = null;
      notify();
    }
    if (!token || !uid) return;
    if (!cachedIds && !loadingPromise) {
      loadingPromise = fetchFavouriteIds(token)
        .then((ids) => {
          // Drop the response if the user has switched again mid-flight.
          if (cachedUserId !== uid) return;
          // Union with any optimistic add that happened while loading,
          // so we never overwrite a pending heart with empty server state.
          const next = new Set(ids);
          if (cachedIds) {
            cachedIds.forEach((id) => next.add(id));
          }
          cachedIds = next;
          notify();
          if (anonCars.length > 0) {
            void mergeAnonymousFavouritesIntoServer(token);
          }
        })
        .catch((err) => {
          console.warn("Failed to load favourite ids:", err);
        })
        .finally(() => {
          loadingPromise = null;
        });
    } else if (cachedIds && anonCars.length > 0) {
      void mergeAnonymousFavouritesIntoServer(token);
    }
  }, [session?.access_token, user?.id]);

  if (session?.access_token) return cachedIds ?? new Set<number>();
  return new Set(anonCars.map((c) => c.id));
}

/**
 * Heart-toggle hook for a single car. Works in both signed-in and
 * anonymous modes — the same `isFavourited` / `toggle` shape, so the
 * call site stays auth-agnostic. Pass `carSnapshot` so the anonymous
 * Favourites page can render the tile without an extra network call.
 */
export function useFavourite(
  carId: number,
  carSnapshot?: AnonymousFavouriteSnapshot | null,
): { isFavourited: boolean; toggle: () => Promise<void>; loading: boolean } {
  const { session, user } = useUser();
  const ids = useFavouriteIds();
  const isFavourited = ids.has(carId);
  const [loading, setLoading] = useState(false);

  const toggle = useCallback(async () => {
    const newState = !isFavourited;
    setLoading(true);
    try {
      if (session?.access_token && user) {
        // Seed the cache if the initial id fetch hasn't resolved yet,
        // so the optimistic add isn't lost when the response lands
        // (useFavouriteIds union-merges in-flight responses against
        // whatever the cache holds at resolution time).
        if (!cachedIds) {
          cachedIds = new Set<number>();
          cachedUserId = user.id;
        }
        if (newState) cachedIds.add(carId);
        else cachedIds.delete(carId);
        notify();
        try {
          await toggleFavourite(session.access_token, carId);
        } catch (err) {
          if (cachedIds) {
            if (newState) cachedIds.delete(carId);
            else cachedIds.add(carId);
            notify();
          }
          console.warn("Failed to toggle favourite:", err);
        }
      } else {
        if (newState) {
          if (!anonCars.some((c) => c.id === carId)) {
            anonCars = [
              {
                id: carId,
                savedAt: Date.now(),
                ...(carSnapshot ?? {}),
              },
              ...anonCars,
            ];
          }
        } else {
          anonCars = anonCars.filter((c) => c.id !== carId);
        }
        persistAnon();
        notify();
      }
    } finally {
      setLoading(false);
    }
  }, [session?.access_token, user, carId, isFavourited, carSnapshot]);

  return { isFavourited, toggle, loading };
}

/**
 * Imperative remove of an anonymous favourite (used by FavouritesPage's
 * tile-level remove button when there is no session).
 */
export function removeAnonymousFavourite(carId: number): void {
  const before = anonCars.length;
  anonCars = anonCars.filter((c) => c.id !== carId);
  if (anonCars.length !== before) {
    persistAnon();
    notify();
  }
}

/**
 * On sign-in, push every anon favourite up to the server (skipping any
 * already-favourited ids) and then clear local storage. Idempotent and
 * safe to call from multiple effects — runs at most once per session.
 */
export async function mergeAnonymousFavouritesIntoServer(token: string): Promise<void> {
  if (mergeInFlight) return mergeInFlight;
  if (anonCars.length === 0) return;
  mergeInFlight = (async () => {
    try {
      let serverIds = cachedIds;
      if (!serverIds) {
        const list = await fetchFavouriteIds(token);
        serverIds = new Set(list);
        cachedIds = serverIds;
      }
      // Walk the local list and only drop the rows we actually managed
      // to push (or that were already on the server). Anything that
      // fails stays in localStorage and will be retried on the next
      // sign-in / token-refresh cycle — we never silently drop a user's
      // saved list because of a transient API hiccup.
      const remaining: AnonymousFavouriteCar[] = [];
      for (const c of anonCars) {
        if (serverIds.has(c.id)) {
          continue;
        }
        try {
          await toggleFavourite(token, c.id);
          serverIds.add(c.id);
        } catch (err) {
          console.warn("Failed to merge anon favourite", c.id, err);
          remaining.push(c);
        }
      }
      anonCars = remaining;
      persistAnon();
      notify();
    } finally {
      mergeInFlight = null;
    }
  })();
  return mergeInFlight;
}

export function clearFavouriteCache(): void {
  cachedIds = null;
  cachedUserId = null;
  loadingPromise = null;
}
